'use client';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { classifyFailure, type SaveFailure } from './saveFeedback';
import { authGeneration } from './token';
import type { AutosaveUnchanged, SaveStatus, UseAutoSaveStatusOptions, UseAutoSaveStatusResult } from 'elowen-plugin-ui-kit';
export type { AutoSaveClassification, SaveStatus, UseAutoSaveStatusOptions } from 'elowen-plugin-ui-kit';

/** What a save callback returns when it compared its actual payload with the accepted baseline and found
 * nothing to send. No request was made, so no attempt settles and nothing is confirmed. */
export const AUTOSAVE_UNCHANGED: AutosaveUnchanged = Object.freeze({ autosave: 'unchanged' } as const);

export type AutoSaveStatusOptions = UseAutoSaveStatusOptions;
/** The published result plus the host-only `cancel` (`reset` is published). */
export interface AutoSaveStatusResult extends UseAutoSaveStatusResult {
  /** Drop pending work and feedback while the accepted baseline stays what it was: for an owner that is about
   *  to write the pending edit itself. */
  cancel: () => void;
}

/** Whether persistence needs a retry before a draft can be dismissed. */
export function saveHalted(status: SaveStatus | undefined): boolean {
  return status === 'error' || status === 'unconfirmed';
}

/** Optional flush preserves editors with immediate parent-owned persistence. */
export async function closeAfterFlush(onClose: () => void, flush?: () => Promise<SaveStatus>): Promise<void> {
  if (!saveHalted(await flush?.())) onClose();
}

type SaveResult = unknown;

function activationIsPending(result: SaveResult): boolean {
  return typeof result === 'object' && result !== null && 'pending' in result && result.pending === true;
}

function sameDeps(a: readonly unknown[] | null, b: readonly unknown[]): boolean {
  return a !== null && a.length === b.length && a.every((value, index) => Object.is(value, b[index]));
}

interface Outcome {
  status: SaveStatus;
  attempt: number;
  failure: SaveFailure | null;
}

/**
 * Debounced auto-persist with a visible status, stale-response protection, and a flush hook — the
 * shared race-safe auto-save controller. Runs `save` shortly after any of `deps` change, but never
 * for the seed value; `ready` gates it until the form has been seeded from the server.
 *
 * `savable` is the form's current validity and is deliberately separate from `ready`. Invalid edits
 * cancel pending work and cannot be persisted through either the debounce, flush, or retry paths.
 *
 * No-op edits write nothing. The controller keeps the ACCEPTED baseline (the seed, then the dependency
 * snapshot of every successful or `AUTOSAVE_UNCHANGED` pass): an edit reverted to it cancels the debounce
 * and never reaches `save`, and a `save` that compares its own payload may answer `AUTOSAVE_UNCHANGED`.
 * Neither settles an attempt, so no confirmation is owed.
 *
 * A conflict, or a write that may have been applied, HOLDS the controller: typing, reverting, invalidating, a
 * queued pass and the close-time flush send nothing and never erase the failure (not even behind `saving`),
 * until the owner re-reads (`reset`), takes the write over (`cancel`) or retries explicitly (`retry`, which then
 * writes the current value, a reverted one included). An ordinary failed edit that is reverted or abandoned clears.
 */
export function useAutoSaveStatus(
  deps: readonly unknown[],
  save: () => Promise<SaveResult> | SaveResult,
  opts: AutoSaveStatusOptions,
): AutoSaveStatusResult {
  const { ready = true, savable = true, delay = 800 } = opts;
  // The controller cannot offer a safe recovery for a conflict or a write that may have been applied without
  // the owner's re-read, and learning that only when the connection drops would be too late.
  if (typeof opts.reload !== 'function') throw new Error('useAutoSaveStatus needs the owner\'s reload: the recovery of a conflict or of a write that may have been applied.');
  const owner = useId();
  const reloadRef = useRef(opts.reload);
  reloadRef.current = opts.reload;
  const seeded = useRef(false);
  const saveRef = useRef(save);
  saveRef.current = save;
  const classifyRef = useRef(opts.classify);
  classifyRef.current = opts.classify;
  const readyRef = useRef(ready);
  const savableRef = useRef(savable);
  readyRef.current = ready;
  savableRef.current = savable;

  const [outcome, setOutcome] = useState<Outcome>({ status: 'idle', attempt: 0, failure: null });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef(false);
  const editGeneration = useRef(authGeneration());
  const [editing, setEditing] = useState(false);
  const running = useRef(false);
  const queued = useRef(false);
  const activeRun = useRef<Promise<SaveStatus> | null>(null);
  // The unmount flush deliberately leaves a save in flight after the component is gone, so its result
  // lands on a hook nobody renders any more. Reporting into that void is not just wasted work.
  const mounted = useRef(true);
  const statusRef = useRef<SaveStatus>('idle');
  const attemptRef = useRef(0);
  const failureRef = useRef<SaveFailure | null>(null);
  // A `reset` bumps the nonce; the effect that sees a new nonce adopts the render's values as the baseline.
  const [reseedNonce, setReseedNonce] = useState(0);
  const reseeded = useRef(0);
  const observedDeps = useRef<readonly unknown[] | null>(null);
  const observedSavable = useRef(savable);
  // The dependency snapshot the server is known to hold: the seed, then each acknowledged pass.
  const accepted = useRef<readonly unknown[] | null>(null);
  const reportStatus = useCallback((next: SaveStatus, failure?: SaveFailure | null, wrote = false) => {
    statusRef.current = next;
    if (wrote) attemptRef.current += 1;
    if (failure !== undefined) failureRef.current = failure;
    else if (next !== 'error' && next !== 'unconfirmed') failureRef.current = null;
    if (mounted.current) setOutcome({ status: next, attempt: attemptRef.current, failure: failureRef.current });
  }, []);

  const setPending = useCallback((next: boolean) => {
    pending.current = next;
    if (mounted.current) setEditing(next);
  }, []);

  // A conflict, or a write that may have been applied, HOLDS the controller. Held, nothing writes by itself: not an
  // edit's debounce, not a queued pass, not a close-time flush, and the failure stays reported however the value
  // moves. Only the owner ends it: `reset` (it re-read), `cancel` (it takes the write over) or an explicit `retry`
  // (the owner's chosen recovery, such as "Keep my changes"). The ref, not the status, is the truth, because a
  // pending edit reports `saving` over a failure that is still owed.
  const heldRef = useRef(false);

  const atBaseline = useCallback((current: readonly unknown[] | null): boolean =>
    !running.current && accepted.current !== null && current !== null && sameDeps(accepted.current, current), []);

  const retryRef = useRef<() => Promise<void>>(async () => {});
  const failureFor = useCallback((error: unknown): SaveFailure => {
    const classified = classifyFailure(error, {
      reload: () => reloadRef.current(),
      retry: () => retryRef.current(),
      classify: classifyRef.current,
    });
    return { owner, attempt: attemptRef.current + 1, cause: error, ...classified } as SaveFailure;
  }, [owner]);

  /** `explicit`: the owner chose to write (a retry or a recovery's merge), as opposed to the debounce, a queued
   *  pass or the close-time flush, which never write while the controller is held. */
  const run = useCallback((explicit = false): Promise<SaveStatus> => {
    // Retry and flush share this guard with the debounce path. A failed valid edit must not be able to
    // write a later invalid value merely because its Retry button survived one render.
    if (!readyRef.current || !savableRef.current) {
      setPending(false);
      return Promise.resolve(statusRef.current);
    }

    setPending(false);
    if (heldRef.current && !explicit) return Promise.resolve(statusRef.current);
    // While a conflict or an unconfirmed write is unresolved the accepted baseline is not what the server holds,
    // so the deliberate retry/merge that reaches this point must write the current values.
    const unsettled = heldRef.current;
    heldRef.current = false;
    // An edit reverted to the accepted baseline is not a change: no request, no attempt. A stale error
    // belonged to the edit that is gone.
    if (!queued.current && !unsettled && atBaseline(observedDeps.current)) {
      if (statusRef.current === 'error') reportStatus('idle');
      return Promise.resolve(statusRef.current);
    }
    queued.current = true;
    const prior = statusRef.current;
    reportStatus('saving');
    if (running.current) return activeRun.current ?? Promise.resolve(statusRef.current);

    running.current = true;
    const operation = (async () => {
      let terminal: SaveStatus = 'saved';
      let wrote = false;
      let failure: SaveFailure | null = null;
      let savedDeps = observedDeps.current;
      // While a write may have been applied, or the server moved on (conflict), only writing settles the question:
      // nothing is "unchanged" against the stale baseline.
      let ambiguous = unsettled;
      // A rapid burst never creates a request pile-up: changes made while saving collapse into one
      // queued pass, and that pass reads the latest callback/state through saveRef.
      while (queued.current) {
        queued.current = false;
        // Teardown flushes may outlive the account that edited the draft. Check every pass, including
        // one queued behind an in-flight request, before the callback can read the next account's cache.
        if (editGeneration.current !== authGeneration()) {
          terminal = 'idle';
          break;
        }
        savedDeps = observedDeps.current;
        if (!ambiguous && accepted.current !== null && sameDeps(accepted.current, savedDeps ?? [])) continue;
        try {
          const result = await saveRef.current();
          accepted.current = savedDeps;
          failure = null;
          ambiguous = false;
          if (result === AUTOSAVE_UNCHANGED) continue;
          wrote = true;
          terminal = activationIsPending(result) ? 'pending' : 'saved';
        } catch (error) {
          failure = failureFor(error);
          ambiguous = failure.kind === 'unconfirmed';
          if (failure.kind === 'conflict' || failure.kind === 'unconfirmed') {
            // What was queued or typed meanwhile is not sent over an unresolved outcome.
            heldRef.current = true;
            queued.current = false;
            if (timer.current) clearTimeout(timer.current);
            timer.current = null;
            setPending(false);
          }
          wrote = true;
          terminal = failure.kind === 'unconfirmed' ? 'unconfirmed' : 'error';
        }
      }
      running.current = false;
      // A newer debounced edit may already be waiting even though this request finished. Never flash
      // "Saved" for the older snapshot; an invalid current value is not saved at all.
      // Adopting the acknowledged baseline may turn savable off without changing the edited values.
      // That is still a successful save; a genuinely different invalid draft remains idle.
      const acceptedBaseline = terminal === 'saved' && sameDeps(savedDeps, observedDeps.current ?? []);
      const halted = terminal === 'error' || terminal === 'unconfirmed';
      let reported: SaveStatus;
      // A conflict or a possibly applied write owes its recovery even when the response arrives after the field
      // became invalid or while a newer edit waited; an ordinary failure belonged to the edit that is gone.
      if (heldRef.current && halted) reported = terminal;
      else if (!savableRef.current && !acceptedBaseline) reported = 'idle';
      else if (pending.current) reported = 'saving';
      else if (!wrote && terminal === 'saved') reported = prior === 'saving' || prior === 'error' || prior === 'unconfirmed' ? 'idle' : prior;
      else reported = terminal;
      reportStatus(reported, halted && reported === terminal ? failure : null, wrote);
      return reported;
    })();
    activeRun.current = operation;
    void operation.finally(() => {
      if (activeRun.current === operation) activeRun.current = null;
    });
    return operation;
  }, [atBaseline, failureFor, reportStatus, setPending]);
  retryRef.current = async () => { await run(true); };

  useEffect(() => {
    if (!ready) {
      seeded.current = false;
      observedDeps.current = [...deps];
      observedSavable.current = savable;
      accepted.current = null;
      heldRef.current = false;
      setPending(false);
      queued.current = false;
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
      reportStatus('idle');
      return;
    }
    if (!seeded.current || reseeded.current !== reseedNonce) {
      seeded.current = true;
      reseeded.current = reseedNonce;
      observedDeps.current = [...deps];
      observedSavable.current = savable;
      accepted.current = [...deps];
      return; // consume the server seed
    }
    // React Activity tears effects down and recreates them while preserving refs. Re-entering with the
    // same values is lifecycle, not an edit, and must not replay the snapshot. A validity transition is
    // still meaningful even when the dependency identity itself did not change.
    const depsChanged = !sameDeps(observedDeps.current, deps);
    const savableChanged = observedSavable.current !== savable;
    observedDeps.current = [...deps];
    observedSavable.current = savable;
    if (!depsChanged && !savableChanged) return;
    if (heldRef.current) {
      // Typing, reverting or invalidating cannot answer an unresolved outcome: no write, and the failure stays.
      setPending(false);
      queued.current = false;
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
      return;
    }
    if (!savable) {
      if (!depsChanged && statusRef.current === 'saved') return;
      setPending(false);
      queued.current = false;
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
      reportStatus('idle');
      return;
    }
    // Typing and then restoring the accepted value leaves nothing to save.
    if (atBaseline(deps)) {
      setPending(false);
      queued.current = false;
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
      if (statusRef.current === 'error') reportStatus('idle');
      return;
    }
    editGeneration.current = authGeneration();
    setPending(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = null; void run(); }, delay);
    return () => { if (timer.current) clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, savable, run, reportStatus, setPending, atBaseline, delay, reseedNonce, ...deps]);

  const flush = useCallback(async (): Promise<SaveStatus> => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (pending.current) return run();
    if (activeRun.current) return activeRun.current;
    return statusRef.current;
  }, [run]);

  // Flush a pending save on teardown so closing a modal never drops the last edit. The flag is cleared in
  // the same cleanup, immediately before the flush, so the write still happens while its now-invisible
  // status updates are dropped.
  useEffect(() => {
    mounted.current = true;
    setOutcome({ status: statusRef.current, attempt: attemptRef.current, failure: failureRef.current });
    setEditing(pending.current);
    return () => { mounted.current = false; void flush(); };
  }, [flush]);

  const retry = useCallback(async () => { await run(true); }, [run]);
  const cancel = useCallback(() => {
    heldRef.current = false;
    setPending(false);
    queued.current = false;
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    reportStatus('idle');
  }, [reportStatus, setPending]);
  const reset = useCallback(() => {
    cancel();
    // The baseline is unknown until the render that carries the loaded state adopts it (see the seed effect).
    accepted.current = null;
    setReseedNonce((current) => current + 1);
  }, [cancel]);
  return { status: outcome.status, editing, attempt: outcome.attempt, failures: outcome.failure ? [outcome.failure] : NO_FAILURES, retry, flush, reset, cancel };
}

const NO_FAILURES: readonly SaveFailure[] = [];
